import { useState, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  getAlerts, getChains, getTimelineSummary,
  runPLN, runMonitor, getMkgStatus, getMe, updateAlertStatus,
} from '../lib/api'
import clsx from 'clsx'
import {
  RefreshCw, Brain, ArrowRight, AlertTriangle, Zap, Info,
  Activity, HeartPulse, Dna, Moon, Footprints, FlaskConical,
  Cpu, ChevronRight,
} from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Alert {
  id: string
  alert_type: string
  urgency: number
  message: string
  action: string
  confidence: number
  status: string
  created_at: string
  mkg_citations: string[]
  evidence_ids: string[]
}

interface Chain {
  id: string
  antecedent_type: string
  consequent_type: string
  lag_days_min: number
  lag_days_max: number
  strength: number
  count: number
  status: string
  mkg_citation: string
  description: string
}

type RunPhase =
  | 'idle'          // never run yet — show prompt
  | 'pln'           // PLN in progress
  | 'monitor'       // monitor in progress
  | 'done'          // complete

// ─── Helpers ─────────────────────────────────────────────────────────────────

const atomLabel = (t: string) => t.replace('Atom', '').replace(/_/g, ' ')
const pct = (n: number) => `${Math.round(n * 100)}%`

// Map atom types to icons and colors
const ATOM_META: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  ActivityAtom:    { icon: Activity,    color: 'text-blue-600',   bg: 'bg-blue-50' },
  HeartRateAtom:   { icon: HeartPulse,  color: 'text-red-500',    bg: 'bg-red-50' },
  HRVAtom:         { icon: HeartPulse,  color: 'text-pink-500',   bg: 'bg-pink-50' },
  SleepAtom:       { icon: Moon,        color: 'text-indigo-500', bg: 'bg-indigo-50' },
  GaitAtom:        { icon: Footprints,  color: 'text-teal-500',   bg: 'bg-teal-50' },
  BiomarkerAtom:   { icon: FlaskConical,color: 'text-amber-600',  bg: 'bg-amber-50' },
  SymptomAtom:     { icon: Zap,         color: 'text-orange-500', bg: 'bg-orange-50' },
  NutrientAtom:    { icon: Dna,         color: 'text-green-600',  bg: 'bg-green-50' },
  MedicationAtom:  { icon: FlaskConical,color: 'text-violet-600', bg: 'bg-violet-50' },
  ContextAtom:     { icon: Brain,       color: 'text-gray-500',   bg: 'bg-gray-100' },
  AdherenceAtom:   { icon: ChevronRight,color: 'text-cyan-600',   bg: 'bg-cyan-50' },
}

const atomMeta = (type: string) =>
  ATOM_META[type] ?? { icon: Brain, color: 'text-gray-400', bg: 'bg-gray-100' }

// Build a human-readable "derived from" sentence for a chain
function chainSourceSentence(chain: Chain): string {
  const ant = atomLabel(chain.antecedent_type)
  const con = atomLabel(chain.consequent_type)
  const n = chain.count
  const lag = chain.lag_days_max === 0
    ? 'same day'
    : `${chain.lag_days_min}–${chain.lag_days_max} days later`
  return `Found ${n} observation${n !== 1 ? 's' : ''} where ${ant} changes preceded ${con} changes ${lag}.`
}

// Build a "derived from" sentence for an alert
const ALERT_ATOMS: Record<string, string[]> = {
  TREND:        ['BiomarkerAtom'],
  PRECURSOR:    ['BiomarkerAtom', 'ActivityAtom'],
  INTERACTION:  ['SleepAtom', 'HRVAtom', 'BiomarkerAtom'],
  CAREGIVER:    ['GaitAtom'],
  MISSED_CHECK: ['BiomarkerAtom'],
}

function alertSourceSentence(alert: Alert): string {
  const atoms = ALERT_ATOMS[alert.alert_type] ?? ['BiomarkerAtom']
  const atomNames = atoms.map(atomLabel).join(' + ')
  const evidenceNote = alert.evidence_ids?.length
    ? ` across ${alert.evidence_ids.length} data point${alert.evidence_ids.length !== 1 ? 's' : ''}`
    : ''
  return `Derived from ${atomNames} readings${evidenceNote}.`
}

const urgencyBorder = (u: number) => {
  if (u >= 4) return 'border-red-400 bg-red-50'
  if (u >= 3) return 'border-orange-400 bg-orange-50'
  return 'border-blue-300 bg-blue-50'
}
const urgencyIcon = (u: number) => {
  if (u >= 4) return <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
  if (u >= 3) return <Zap size={16} className="text-orange-500 shrink-0 mt-0.5" />
  return <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
}

// ─── Atom chip ────────────────────────────────────────────────────────────────

function AtomChip({ type }: { type: string }) {
  const { icon: Icon, color, bg } = atomMeta(type)
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', bg, color)}>
      <Icon size={10} />
      {atomLabel(type)}
    </span>
  )
}

// ─── Animated chain card ──────────────────────────────────────────────────────

function AnimatedChainCard({ chain, delay }: { chain: Chain; delay: number }) {
  const [visible, setVisible] = useState(false)
  const [showSource, setShowSource] = useState(false)

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), delay)
    const t2 = setTimeout(() => setShowSource(true), delay + 300)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [delay])

  const antMeta = atomMeta(chain.antecedent_type)
  const conMeta = atomMeta(chain.consequent_type)
  const AntIcon = antMeta.icon
  const ConIcon = conMeta.icon

  const statusCls = chain.status === 'confirmed'
    ? 'bg-green-100 text-green-700'
    : chain.status === 'candidate'
    ? 'bg-yellow-100 text-yellow-700'
    : 'bg-gray-100 text-gray-500'

  return (
    <div
      className={clsx(
        'bg-white border border-gray-200 rounded-xl p-4 shadow-sm transition-all duration-500',
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
      )}
    >
      {/* Atoms → arrow → atoms */}
      <div className="flex items-center gap-2 mb-2">
        <span className={clsx('flex items-center gap-1 text-xs font-semibold', antMeta.color)}>
          <AntIcon size={13} />
          {atomLabel(chain.antecedent_type)}
        </span>
        <ArrowRight size={13} className="text-gray-300" />
        <span className={clsx('flex items-center gap-1 text-xs font-semibold', conMeta.color)}>
          <ConIcon size={13} />
          {atomLabel(chain.consequent_type)}
        </span>
        <span className={clsx('ml-auto text-xs px-2 py-0.5 rounded-full font-medium', statusCls)}>
          {chain.status}
        </span>
      </div>

      <p className="text-xs text-gray-500 mb-2">{chain.description}</p>

      <div className="flex gap-3 text-xs text-gray-400 mb-2">
        <span>Lag: {chain.lag_days_min}–{chain.lag_days_max}d</span>
        <span>Strength: {pct(chain.strength)}</span>
        <span>n={chain.count}</span>
      </div>

      {/* Source sentence — fades in slightly after card */}
      <div className={clsx(
        'transition-all duration-400 overflow-hidden',
        showSource ? 'max-h-10 opacity-100' : 'max-h-0 opacity-0',
      )}>
        <p className="text-xs text-brand-500 italic border-t border-gray-100 pt-1.5">
          ↳ {chainSourceSentence(chain)}
        </p>
      </div>
    </div>
  )
}

// ─── Animated alert card ──────────────────────────────────────────────────────

function AnimatedAlertCard({
  alert, delay, onDismiss, onActedOn,
}: {
  alert: Alert
  delay: number
  onDismiss: (id: string) => void
  onActedOn: (id: string) => void
}) {
  const [visible, setVisible] = useState(false)
  const [showSource, setShowSource] = useState(false)

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), delay)
    const t2 = setTimeout(() => setShowSource(true), delay + 300)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [delay])

  const sourceAtoms = ALERT_ATOMS[alert.alert_type] ?? ['BiomarkerAtom']

  return (
    <div
      className={clsx(
        'border-l-4 rounded-xl p-4 transition-all duration-500',
        urgencyBorder(alert.urgency),
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
      )}
    >
      <div className="flex items-start gap-3">
        {urgencyIcon(alert.urgency)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-wide text-gray-600">
              {alert.alert_type}
            </span>
            <span className="text-xs text-gray-400">{pct(alert.confidence)} confidence</span>
          </div>
          <p className="text-sm text-gray-800 leading-relaxed">{alert.message}</p>
          {alert.action && (
            <p className="text-sm text-gray-600 mt-1 font-medium">→ {alert.action}</p>
          )}
          {alert.mkg_citations?.length > 0 && (
            <p className="text-xs text-gray-400 mt-1">{alert.mkg_citations.join(' · ')}</p>
          )}

          {/* Source atoms + derivation sentence */}
          <div className={clsx(
            'transition-all duration-400 overflow-hidden',
            showSource ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0',
          )}>
            <div className="mt-2 pt-2 border-t border-gray-200/60">
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <span className="text-xs text-gray-400">From:</span>
                {sourceAtoms.map(a => <AtomChip key={a} type={a} />)}
              </div>
              <p className="text-xs text-gray-400 italic">{alertSourceSentence(alert)}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-3 ml-7">
        <button
          onClick={() => onActedOn(alert.id)}
          className="text-xs px-3 py-1 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
        >
          Acted on
        </button>
        <button
          onClick={() => onDismiss(alert.id)}
          className="text-xs px-3 py-1 rounded-lg bg-white/60 text-gray-500 hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}

// ─── Step log line ────────────────────────────────────────────────────────────

function LogLine({ text, done }: { text: string; done: boolean }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {done
        ? <span className="text-green-500 font-bold">✓</span>
        : <RefreshCw size={10} className="text-brand-400 animate-spin" />}
      <span className={done ? 'text-gray-600' : 'text-brand-500 font-medium'}>{text}</span>
    </div>
  )
}

// ─── Empty / prompt state ─────────────────────────────────────────────────────

function EmptyPrompt({ onRun, isPending }: { onRun: () => void; isPending: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center mb-5">
        <Brain size={30} className="text-brand-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">No results yet</h3>
      <p className="text-sm text-gray-400 max-w-sm leading-relaxed mb-6">
        Click <span className="font-semibold text-gray-600">Run reasoning</span> to analyse your health
        atoms, discover causal patterns with PLN, and fire the monitor rules.
      </p>
      <button
        onClick={onRun}
        disabled={isPending}
        className="flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors shadow"
      >
        <RefreshCw size={15} className={isPending ? 'animate-spin' : ''} />
        Go ahead — Run reasoning
      </button>
    </div>
  )
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function Dashboard() {
  const qc = useQueryClient()

  const [phase, setPhase] = useState<RunPhase>('idle')
  const [hasEverRun, setHasEverRun] = useState(false)
  const [revealedChains, setRevealedChains] = useState<Chain[]>([])
  const [revealedAlerts, setRevealedAlerts] = useState<Alert[]>([])
  const [log, setLog] = useState<{ text: string; done: boolean }[]>([])
  const runCount = useRef(0)

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => getMe().then(r => r.data) })
  const personId = me?.id

  const { data: summary } = useQuery({
    queryKey: ['timeline-summary'],
    queryFn: () => getTimelineSummary().then(r => r.data),
  })

  const { data: mkgStatus } = useQuery({
    queryKey: ['mkg-status'],
    queryFn: () => getMkgStatus().then(r => r.data),
  })

  const atomCount = summary
    ? Object.values(summary as Record<string, number>).reduce((a, b) => a + b, 0)
    : 0

  const addLog = (text: string) =>
    setLog(prev => [...prev, { text, done: false }])

  const doneLog = (text: string) =>
    setLog(prev => prev.map(l => l.text === text ? { ...l, done: true } : l))

  const revealItems = <T,>(
    items: T[],
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    intervalMs: number,
  ) =>
    new Promise<void>(resolve => {
      let i = 0
      const tick = () => {
        if (i >= items.length) { resolve(); return }
        setter(prev => [...prev, items[i]])
        i++
        setTimeout(tick, intervalMs)
      }
      tick()
    })

  const handleRun = async () => {
    if (!personId) return
    runCount.current += 1
    setPhase('pln')
    setHasEverRun(true)
    setRevealedChains([])
    setRevealedAlerts([])
    setLog([])

    // ── PLN phase ──
    addLog('Scanning atom history with PLN…')
    let chains: Chain[] = []
    try {
      await runPLN(365)
      const res = await qc.fetchQuery({
        queryKey: ['chains', personId],
        queryFn: () => getChains(personId).then(r => r.data),
        staleTime: 0,
      })
      chains = (Array.isArray(res) ? res : []).filter(Boolean) as Chain[]
      doneLog('Scanning atom history with PLN…')
    } catch {
      doneLog('Scanning atom history with PLN…')
    }

    addLog(`Revealing ${chains.length} causal chains…`)
    await revealItems(chains.slice(0, 10), setRevealedChains, 280)
    doneLog(`Revealing ${chains.length} causal chains…`)

    // ── Monitor phase ──
    setPhase('monitor')
    addLog('Running monitor alert rules…')
    let alerts: Alert[] = []
    try {
      await runMonitor()
      const res = await qc.fetchQuery({
        queryKey: ['alerts', 'active', personId],
        queryFn: () => getAlerts(personId, 'active').then(r => r.data),
        staleTime: 0,
      })
      alerts = (Array.isArray(res) ? res : []).filter(Boolean) as Alert[]
      doneLog('Running monitor alert rules…')
    } catch {
      doneLog('Running monitor alert rules…')
    }

    addLog(`Revealing ${alerts.length} active alerts…`)
    await revealItems(alerts.slice(0, 5), setRevealedAlerts, 350)
    doneLog(`Revealing ${alerts.length} active alerts…`)

    setPhase('done')
    qc.invalidateQueries({ queryKey: ['alerts'] })
    qc.invalidateQueries({ queryKey: ['chains'] })
  }

  const dismissAlert = async (id: string) => {
    await updateAlertStatus(id, 'dismissed')
    setRevealedAlerts(prev => prev.filter(a => a.id !== id))
  }

  const actedOnAlert = async (id: string) => {
    await updateAlertStatus(id, 'acted_on')
    setRevealedAlerts(prev => prev.filter(a => a.id !== id))
  }

  const isPending = phase === 'pln' || phase === 'monitor'
  const navigate = useNavigate()

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-5 sm:mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-sm text-gray-500">Your personal health reasoning overview</p>
        </div>
        <button
          onClick={handleRun}
          disabled={isPending}
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors shadow-sm"
        >
          <RefreshCw size={14} className={isPending ? 'animate-spin' : ''} />
          Run reasoning
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 sm:mb-8">
        {[
          { label: 'Total atoms',   value: atomCount,                                              to: '/app/graph' },
          { label: 'Active alerts', value: revealedAlerts.length || (hasEverRun ? 0 : '—'),        to: '/app/alerts' },
          { label: 'Causal chains', value: revealedChains.length || (hasEverRun ? 0 : '—'),        to: null },
          { label: 'DAS engine',    value: mkgStatus?.das_type ?? '…',                             to: '/app/engine' },
        ].map(({ label, value, to }) => (
          <div
            key={label}
            onClick={() => to && navigate(to)}
            className={clsx(
              'bg-white border border-gray-200 rounded-xl p-4 shadow-sm transition-colors',
              to ? 'cursor-pointer hover:border-brand-400 hover:shadow-md group' : '',
            )}
          >
            <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
            <p className={clsx(
              'text-2xl font-bold mt-1 transition-colors',
              to ? 'text-gray-900 group-hover:text-brand-600' : 'text-gray-900',
            )}>
              {value}
            </p>
          </div>
        ))}
      </div>

      {/* ── Never run: full-page prompt ── */}
      {!hasEverRun && (
        <EmptyPrompt onRun={handleRun} isPending={isPending} />
      )}


      {/* ── Has run: live log + results ── */}
      {hasEverRun && (
        <>
          {/* Live step log */}
          {log.length > 0 && (
            <div className="mb-6 bg-gray-950 rounded-xl px-5 py-4 space-y-1.5 border border-gray-800">
              {log.map((l, i) => <LogLine key={i} text={l.text} done={l.done} />)}
              {phase === 'done' && (
                <p className="text-xs text-green-400 font-semibold mt-1">
                  ✓ Reasoning complete
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Alerts column */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Cpu size={14} /> Active Alerts
              </h3>

              {phase === 'pln' && (
                <p className="text-sm text-gray-400 italic">Running PLN first…</p>
              )}

              {(phase === 'monitor' || phase === 'done') && revealedAlerts.length === 0 && (
                <p className="text-sm text-gray-400 italic flex items-center gap-1">
                  <RefreshCw size={12} className="animate-spin text-brand-400" />
                  Evaluating alert rules…
                </p>
              )}

              <div className="space-y-3">
                {revealedAlerts.filter(a => a?.id).map((a, i) => (
                  <AnimatedAlertCard
                    key={a.id}
                    alert={a}
                    delay={i * 350}
                    onDismiss={dismissAlert}
                    onActedOn={actedOnAlert}
                  />
                ))}
              </div>
            </div>

            {/* Chains column */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Brain size={14} /> Causal Chains
              </h3>

              {phase === 'pln' && revealedChains.length === 0 && (
                <p className="text-sm text-gray-400 italic flex items-center gap-1">
                  <RefreshCw size={12} className="animate-spin text-brand-400" />
                  Scanning atom history with PLN…
                </p>
              )}

              <div className="space-y-3">
                {revealedChains.filter(c => c?.id).map((c, i) => (
                  <AnimatedChainCard key={c.id} chain={c} delay={i * 280} />
                ))}
              </div>
            </div>

          </div>

          {/* Atom breakdown */}
          {summary && Object.keys(summary).length > 0 && (
            <div className="mt-8">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Atom Breakdown
              </h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(summary as Record<string, number>).map(([type, count]) => (
                  <AtomChip key={type} type={type} />
                ))}
                <span className="text-xs text-gray-400 self-center ml-1">
                  {atomCount} total atoms
                </span>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  )
}
